import React, { FunctionComponent } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, InputLabel, Typography } from '@mui/material';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import { withStyles } from '@mui/styles';
import { useContainer } from 'unstated-next';

import AssignUsers from './AssignUsers';
import BreakoutUserRowItem from './BreakoutUserRowItem';
import DeleteRoom from './DeleteRoom';
import JoinBreakout from './JoinBreakout';
import LeaveBreakout from './LeaveBreakout';
import RenameRoom from './RenameRoom';
import LightTheme from '../../../themes/LightTheme';
import { BreakoutUser } from '../../../types/breakoutRoomTypes';
import BreakoutRoomOptionsContainer from '../BreakoutRoomOptionsContainer';
import { BreakoutCreateMode } from '../enums/BreakoutCreateMode';
import { StyledRowItemsRightJustContainer, StyledRowItemContainer } from '../styles/styled-components';

interface BreakoutRoomRowProps {
    roomName: string;
    roomId: string;
    roomUsers: BreakoutUser[];
}

const RoomLabel = withStyles(() => ({
    root: {
        color: LightTheme.colors.primary.main,
        lineHeight: '24px',
        fontSize: '18px',
        textAlign: 'left',
        fontWeight: 'bold'
    }
}))(InputLabel);

const AccordionSummary = withStyles({
    root: {
        flexDirection: 'row-reverse'
    }
})(MuiAccordionSummary);

const BreakoutRoomRowItem: FunctionComponent<BreakoutRoomRowProps> = ({ roomName, roomId, roomUsers }) => {
    const [expanded, setExpanded] = React.useState<string | false>();
    const { isCreator, unassignedUsers, roomsOpen, currentRoom, createMode } =
        useContainer(BreakoutRoomOptionsContainer);

    const handleChange = (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
        if (createMode === BreakoutCreateMode.AllowChoice) {
            setExpanded(false); // when users are allowed to choose room, prevent showing room details
        } else {
            setExpanded(newExpanded ? panel : false);
        }
    };

    const userCount = roomUsers?.length ?? 0;

    const roomLabel = createMode === BreakoutCreateMode.AllowChoice ? roomName : `${roomName} (${userCount})`;

    const renderUsers = () => {
        if (roomUsers?.length > 0) {
            return roomUsers?.map((user) => (
                <BreakoutUserRowItem
                    key={user.breakoutUserId}
                    name={user.name}
                    id={user.breakoutUserId}
                    imgSource={user.imageUrl}
                    inBreakoutRoom={user.active}
                />
            ));
        }
    };

    return (
        <>
            <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                <AccordionSummary
                    expandIcon={createMode !== BreakoutCreateMode.AllowChoice && <ExpandMoreIcon />}
                    aria-controls="panel1d-content"
                    id="panel1d-header"
                >
                    <StyledRowItemContainer>
                        <RoomLabel>{roomLabel}</RoomLabel>
                        <StyledRowItemsRightJustContainer>
                            {isCreator && !roomsOpen ? <RenameRoom roomId={roomId} roomName={roomName} /> : null}
                            {isCreator && !roomsOpen ? <DeleteRoom roomId={roomId} roomName={roomName} /> : null}
                            {isCreator && createMode !== BreakoutCreateMode.AllowChoice ? (
                                <AssignUsers roomId={roomId} roomName={roomName} unassignedUsers={unassignedUsers} />
                            ) : null}
                            {roomsOpen && currentRoom !== roomName ? (
                                <JoinBreakout roomId={roomId} roomName={roomName} />
                            ) : null}
                            {roomsOpen && currentRoom === roomName ? <LeaveBreakout roomName={roomName} /> : null}
                        </StyledRowItemsRightJustContainer>
                    </StyledRowItemContainer>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography>{renderUsers()}</Typography>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

export default BreakoutRoomRowItem;
